import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiTrash2, FiMapPin, FiFlag } from 'react-icons/fi';

const SupportedRegions = () => {
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchRegions();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchStates(selectedCountry);
    } else {
      setStates([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  const fetchRegions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/regions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRegions(data.data || []);
      } else {
        throw new Error('Failed to fetch supported regions');
      }
    } catch (err) {
      setError('Failed to load supported regions');
      console.error('Fetch regions error:', err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/countries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data.data || []);
      }
    } catch (err) {
      console.error('Fetch countries error:', err);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/states/${countryId}`);
      if (response.ok) {
        const data = await response.json();
        setStates(data.data || []);
      }
    } catch (err) {
      console.error('Fetch states error:', err);
      setStates([]);
    }
  };

  const handleAddRegion = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/regions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          countryId: selectedCountry,
          stateId: selectedState
        })
      });

      if (response.ok) {
        setSuccess('Supported region added successfully');
        setShowAddModal(false);
        setSelectedCountry('');
        setSelectedState('');
        fetchRegions(); // Refresh the list
      } else {
        const errorData = await response.json();
        console.error('Add region error:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to add supported region');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegion = async (regionId) => {
    if (!window.confirm('Are you sure you want to remove this supported region?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/regions/${regionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Supported region removed successfully');
        fetchRegions(); // Refresh the list
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove supported region');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Container className="admin-page">
      <Header className="admin-page-header">
        <div>
          <Title className="admin-page-title">Supported Regions</Title>
          <Subtitle className="admin-page-subtitle">
            Manage countries and states where the platform is available
          </Subtitle>
        </div>
        <AddButton 
          className="admin-btn admin-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <FiPlus /> Add Region
        </AddButton>
      </Header>

      {error && (
        <AlertMessage className="admin-alert admin-alert-error">
          {error}
        </AlertMessage>
      )}

      {success && (
        <AlertMessage className="admin-alert admin-alert-success">
          {success}
        </AlertMessage>
      )}

      <RegionsGrid className="admin-grid admin-grid-2">
        {regions.map((region) => (
          <RegionCard key={region._id} className="admin-glass-card">
            <RegionHeader>
              <RegionIcon>
                <FiMapPin />
              </RegionIcon>
              <RegionInfo>
                <RegionCountry>
                  <FiFlag /> {region.country_id?.name || 'Unknown Country'}
                </RegionCountry>
                <RegionState>
                  {region.state_id?.name || 'Unknown State'}
                </RegionState>
              </RegionInfo>
              <DeleteButton
                onClick={() => handleDeleteRegion(region._id)}
                className="admin-btn-danger"
                title="Remove region"
              >
                <FiTrash2 />
              </DeleteButton>
            </RegionHeader>
            <RegionDetails>
              <DetailItem>
                <strong>Country Code:</strong> {region.country_id?.code || 'N/A'}
              </DetailItem>
              <DetailItem>
                <strong>Added:</strong> {new Date(region.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </DetailItem>
              <DetailItem>
                <strong>Status:</strong> 
                <StatusBadge active={region.is_active}>
                  {region.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </DetailItem>
            </RegionDetails>
          </RegionCard>
        ))}
      </RegionsGrid>

      {regions.length === 0 && (
        <EmptyState className="admin-glass-card">
          <EmptyIcon>🌍</EmptyIcon>
          <EmptyTitle>No Supported Regions</EmptyTitle>
          <EmptyText>
            Start by adding countries and states where your platform is available.
          </EmptyText>
        </EmptyState>
      )}

      {showAddModal && (
        <ModalOverlay className="admin-modal-overlay" onClick={() => setShowAddModal(false)}>
          <Modal className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <ModalHeader className="admin-modal-header">
              <ModalTitle className="admin-modal-title">Add Supported Region</ModalTitle>
            </ModalHeader>

            <form onSubmit={handleAddRegion}>
              <FormGroup className="admin-form-group">
                <FormLabel className="admin-form-label">Country</FormLabel>
                <Select 
                  value={selectedCountry} 
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="admin-select"
                  required
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup className="admin-form-group">
                <FormLabel className="admin-form-label">State/Region</FormLabel>
                <Select 
                  value={selectedState} 
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="admin-select"
                  required
                  disabled={!selectedCountry}
                >
                  <option value="">Select a state</option>
                  {states.map((state) => (
                    <option key={state._id} value={state._id}>
                      {state.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <ModalActions className="admin-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="admin-btn admin-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedCountry || !selectedState}
                  className="admin-btn admin-btn-primary"
                >
                  {loading ? 'Adding...' : 'Add Region'}
                </button>
              </ModalActions>
            </form>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1``;
const Subtitle = styled.p``;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlertMessage = styled.div``;

const RegionsGrid = styled.div``;

const RegionCard = styled.div`
  padding: 24px;
`;

const RegionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const RegionIcon = styled.div`
  background: linear-gradient(135deg, #000000 0%, #374151 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const RegionInfo = styled.div`
  flex: 1;
`;

const RegionCountry = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #111827;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RegionState = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
`;

const DeleteButton = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #b91c1c;
    transform: scale(1.05);
  }
`;

const RegionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailItem = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  
  strong {
    color: #111827;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 8px;
  background: ${props => props.active ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.active ? '#065f46' : '#991b1b'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 40px;
  margin-top: 40px;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  color: #6b7280;
  max-width: 400px;
  margin: 0 auto;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  padding: 0 24px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4b5563;
    box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
  }
  
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
  
  button {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .admin-btn-secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
    }
  }
  
  .admin-btn-primary {
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
  }
`;

export default SupportedRegions;
